export interface InstructionalVideo {
  id: string;
  vimeoId: string;
  title: string;
  description: string;
  duration: string;
  category: 'course' | 'reference';
  module: number;
  orderIndex: number;
  keyTakeaways: string[];
  prerequisites?: string[];
}

// Career Foundation Course - Sequential 6-video series
export const instructionalVideos: InstructionalVideo[] = [
  // Module 1: Career Mindset Foundation
  {
    id: 'mindset-shift',
    vimeoId: '1140544071',
    title: 'Welcome & Career Mindset Shift',
    description: 'Shift from "job" thinking to "career" thinking for meaningful self-assessment and career growth',
    duration: '6:47',
    category: 'course',
    module: 1,
    orderIndex: 1,
    keyTakeaways: [
      'The difference between job roles and comprehensive career impact',
      'Why FutureLens needs deep, career-focused answers for best results',
      'The "Career Lens" exercise (viewing your role through value and impact)',
      'Overcoming common blocks to self-reflection'
    ],
    prerequisites: []
  },
  {
    id: 'memory-mining',
    vimeoId: '1140544049',
    title: 'Mining Your Career Memories',
    description: 'Techniques for recalling hidden accomplishments to accurately fill the BRAG sheet',
    duration: '3:26',
    category: 'course',
    module: 1,
    orderIndex: 2,
    keyTakeaways: [
      'Why people forget their wins and normalize success',
      'Five Memory Mining Techniques: Timeline Walk, Recognition Radar, Problem-Solution Inventory',
      'Comparison Method and Mundane to Meaningful Reframe',
      'Guided journal exercise for memory excavation'
    ],
    prerequisites: ['mindset-shift']
  },

  // Module 2: BRAG Sheet Mastery
  {
    id: 'value-translation',
    vimeoId: '1140544015',
    title: 'Translating Accomplishments to Value',
    description: 'Turn accomplishments into business value using metrics, outcomes, and clear impact statements',
    duration: '7:08',
    category: 'course',
    module: 2,
    orderIndex: 3,
    keyTakeaways: [
      'The Value Translation Formula: what you did + who it helped + what changed',
      'Three Levels of Impact (Task, Outcome, Value) with examples',
      'Finding meaningful metrics (Metrics Menu): time, people, quality, scale',
      'Mistakes to avoid: being vague, overly modest, omitting the "so what?"'
    ],
    prerequisites: ['memory-mining']
  },
  {
    id: 'star-method',
    vimeoId: '1140543991',
    title: 'Crafting Stories Using the STAR Method',
    description: 'Organize and present accomplishments as memorable stories using the STAR approach',
    duration: '3:06',
    category: 'course',
    module: 2,
    orderIndex: 4,
    keyTakeaways: [
      'Why stories matter in interviews, networking, and career growth',
      'Full breakdown: Situation, Task, Action, Result',
      'Real-world STAR examples for different roles',
      'Tips for practicing and refining your stories'
    ],
    prerequisites: ['value-translation']
  },

  // Module 3: DREAM Sheet Vision
  {
    id: 'dream-framework',
    vimeoId: '1140543959',
    title: 'Envisioning Your Career Future (DREAM Framework)',
    description: 'Articulate future aspirations and preferences with the DREAM framework for deeper self-understanding',
    duration: '3:39',
    category: 'course',
    module: 3,
    orderIndex: 5,
    keyTakeaways: [
      'DREAM acronym: Driving Motivations, Role Preferences, Environment Fit, Ambitions, Management Style',
      'Permission to "dream big" and revise as you go',
      'Guided "future self" visualization exercise',
      'Reflective sentence starters for career vision'
    ],
    prerequisites: ['star-method']
  },

  // Module 4: Taking Action
  {
    id: 'bringing-it-together',
    vimeoId: '1140543928',
    title: 'Bringing It All Together & Next Steps',
    description: 'Finalize, reflect, and celebrate your onboarding journey and what comes next',
    duration: '3:29',
    category: 'course',
    module: 4,
    orderIndex: 6,
    keyTakeaways: [
      'Celebrating the achievement of deep career reflection',
      'Taking your time to revisit and refine answers',
      'What happens after input: Career Intelligence Report generated',
      'Using the report for insights, strategy, and action steps',
      'Treating this as ongoing career development, not a rushed task'
    ],
    prerequisites: ['dream-framework']
  }
];

// Module structure for course organization
export const courseModules = [
  {
    id: 1,
    title: 'Career Mindset Foundation',
    description: 'Change how you think about your career before you start writing',
    videos: ['mindset-shift', 'memory-mining'],
    estimatedTime: '14 minutes'
  },
  {
    id: 2,
    title: 'BRAG Sheet Mastery',
    description: 'Learn the methodology for documenting achievements effectively',
    videos: ['value-translation', 'star-method'],
    estimatedTime: '15 minutes'
  },
  {
    id: 3,
    title: 'DREAM Sheet Vision',
    description: 'Guide your career reflection and aspiration setting',
    videos: ['dream-framework'],
    estimatedTime: '8 minutes'
  },
  {
    id: 4,
    title: 'Taking Action',
    description: 'Launch into completing your BRAG and DREAM sheets',
    videos: ['bringing-it-together'],
    estimatedTime: '5 minutes'
  }
];

// Helper functions
export const getVideosByCategory = (category: 'course' | 'reference') => {
  return instructionalVideos.filter((video) => video.category === category);
};

export const getVideoById = (id: string) => {
  return instructionalVideos.find((video) => video.id === id);
};

export const getVideosByModule = (moduleId: number) => {
  return instructionalVideos.filter((video) => video.module === moduleId);
};

export const getCourseVideos = () => {
  return instructionalVideos
    .filter((video) => video.category === 'course')
    .sort((a, b) => a.orderIndex - b.orderIndex);
};

export const getTotalCourseDuration = () => {
  const durations = instructionalVideos.map(v => {
    const parts = v.duration.split(':');
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  });
  const total = durations.reduce((sum, d) => sum + d, 0);
  return `${Math.floor(total / 60)} minutes`;
};

export const getNextVideo = (currentVideoId: string) => {
  const currentIndex = instructionalVideos.findIndex(v => v.id === currentVideoId);
  if (currentIndex === -1 || currentIndex === instructionalVideos.length - 1) {
    return null;
  }
  return instructionalVideos[currentIndex + 1];
};

export const getPreviousVideo = (currentVideoId: string) => {
  const currentIndex = instructionalVideos.findIndex(v => v.id === currentVideoId);
  if (currentIndex <= 0) {
    return null;
  }
  return instructionalVideos[currentIndex - 1];
};

// Instructions for admin to update video IDs
export const VIDEO_SETUP_INSTRUCTIONS = `
To configure your Vimeo videos for the Career Foundation Course:

1. Upload your 6 instructional videos to Vimeo in this order:
   - Video 1: Welcome & Career Mindset Shift (6:30)
   - Video 2: Mining Your Career Memories (7:15)
   - Video 3: Translating Accomplishments to Value (8:45)
   - Video 4: Envisioning Your Career Future (7:30)
   - Video 5: Crafting Stories Using STAR Method (5:45)
   - Video 6: Bringing It All Together (4:15)

2. For each video, copy the video ID from the URL
   Example: https://vimeo.com/123456789 -> Video ID is "123456789"

3. Update the vimeoId fields in this file with your actual video IDs

4. Ensure videos are set to "Public" or "Unlisted" in Vimeo settings

5. Enable embedding in Vimeo video settings

Video IDs to configure:
- YOUR_VIMEO_ID_1: Welcome & Career Mindset Shift
- YOUR_VIMEO_ID_2: Mining Your Career Memories
- YOUR_VIMEO_ID_3: Translating Accomplishments to Value
- YOUR_VIMEO_ID_4: Envisioning Your Career Future
- YOUR_VIMEO_ID_5: Crafting Stories Using STAR Method
- YOUR_VIMEO_ID_6: Bringing It All Together

Total Course Duration: ~42 minutes
Recommended Completion: Before starting BRAG/DREAM sheets
`;
